import { Injectable } from '@angular/core';
import {
  CanActivate, CanActivateChild, Router, ActivatedRouteSnapshot, RouterStateSnapshot,
} from '@angular/router';
import { DataService } from 'AppServices/data.service';
import { AuthHttpService, getCookie } from 'AppServices/authentication/auth-http.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, flatMap, switchMap, tap, map } from 'rxjs/operators';
import { User } from 'SharedModels/app.models';
import * as Sentry from '@sentry/browser';
import { FarmsService } from 'AppServices/farms.service';
import { NEW_PLATFORM_URL } from 'app/app.constants';


@Injectable()
export class AuthGuardService implements CanActivate, CanActivateChild { // , Resolve {

  constructor(
    private router: Router,
    private dataService: DataService,
    private authHttp: AuthHttpService,
    private _farmsService: FarmsService
  ) { }

  getStaffRequestedClient(state, clientId): Observable<boolean> {
    let clientUserId;
    return this.authHttp.get('gateway/clients/' + clientId + '/?fields=user_id').pipe(
      switchMap((res: any) => {
        clientUserId = res.user_id;
        return this._farmsService.getClientFarms(clientId);
      }),
      switchMap(() => {
        return this.getUserData(clientUserId, state, clientId);
      }),
      catchError(() => {
        this.router.navigateByUrl('/log-in?auto_login=false');
        this.dataService.loadingApp = false;
        return of(false);
      })
    );
  }

  private _checkIfLoggedInUserHasAccessToFarmOrRedirectHome(user: User, farmId: string): Observable<void> {
    const myClient = user.clients.find(i => i.user_id === user.id);
    if (myClient && myClient.id) {
      return this._farmsService.getClientFarms(myClient.id).pipe(
        tap(res => {
          const farmIds = new Set(res.farms.map(f => f.farm_id));
          if (!farmIds.has(parseInt(farmId, 10))) {
            window.location.assign(
              window.location.protocol + '//' + window.location.host + '/aeroview/' + myClient.id
            )
          }
        }),
        map(_ => null)
      );
    } else {
      return of(null);
    }
  }

  private _checkIfLoggedInUserClientIdIsInUrlOrRedirect(user: User): void {
    // This is for sharing an Aeroview link with a different user in the snapshot modal.
    // Staff users are not affected by this.
    const myClient = user.clients.find(i => i.user_id === user.id);
    const myClientId = myClient && myClient.id;
    if (myClientId) {
      const urlSplitQueryParams = window.location.href.split('?')
      const queryParams = urlSplitQueryParams[1];
      let url = urlSplitQueryParams[0].split('/');
      if (url[4] !== myClientId.toString()) {
        url[4] = myClientId.toString();
        window.location.assign(`${url.join('/')}?${queryParams ? queryParams : ''}`)
      }
    }
  }

  private _updateUserInformationInDataCache(user: User) {
    // If the data service staff is already true then the returned user is another clients user
    if (this.dataService.staff === true) {
      this.dataService.staffUser = JSON.parse(JSON.stringify(this.dataService.user));
    }
    this.dataService.user = user;
    if (this.dataService.staff === false) {
      this.dataService.staff = this.dataService.user.is_staff;
      // Update the raven config to point to this user
      Sentry.configureScope((scope) => {
        scope.setUser({
          email: this.dataService.user.email,
          id: this.dataService.user.id.toString()
        });
      });
      this.dataService.updateBeaconInformation(user);
    }
    // Check whether the user is "outsourced" or not
    this.dataService.determineIfOutsourcedUser();
    // Confirm that there has been a user
    this.dataService.userConfirmed = true;
  }

  /** Function to handle routing staff members to the relevant tool (THIS SHOULD HOPEFULLY GET DEPRECATED SOON) **/
  private _handleRoutingOfStaffMembersOutOfAeroview(): boolean {
    this.dataService.determineStaffAccess();
    // Route the user to the correct internal tools page if necessary
    if (
      window.location.href.includes('internal-tools') && !window.location.href.includes('localhost') && !window.location.href.includes('dev')
    ) {
      window.location.href = window.location.href
        .replace('https://aerobotics', 'https://internal-tools.aerobotics')
        .replace('app.', 'internal-tools.')
        .replace('www', 'internal-tools')
        .replace('staging', 'internal-tools');
    }
    return false;
  }

  getUserData(userId, state, staffRequestsClient): Observable<boolean> {
    // Extract the relevant parameters from the url
    const clientId = state.url.split('/')[2];
    const fourthIndexSplit = state.url.split('/')[3];
    let policyId, farmId;
    // We're either the "client" level page (to choose farm, etc) or on the child views relating to a single farm or policy
    if (fourthIndexSplit === 'policy') {
      policyId = state.url.split('/')[4];
    } else {
      // Not going to bother fixing this properly by splitting out client-view auth guard vs farm-view auth guard
      const fourthParam = state.url.split('/')[3];
      if (fourthParam && !isNaN(parseInt(fourthParam))) { // fourth param is a valid farm id
        farmId = state.url.split('/')[3];
      }
    }
    return this.authHttp.get('gateway/users/' + userId + '/').pipe(
      switchMap((user: User) => {
        if (farmId) {
          return forkJoin([
            of(user),
            this._checkIfLoggedInUserHasAccessToFarmOrRedirectHome(user, farmId)
          ]);
        }
        return of([user, null]);
      }),
      tap(([user, _]: [User, any]) => {
        this._checkIfLoggedInUserClientIdIsInUrlOrRedirect(user);
        this._updateUserInformationInDataCache(user);
      }),
      switchMap(() => {
        // If there is a client id in the url, then fetch the specific client for the staff member
        if (isNaN(clientId) === false && this.dataService.staff && staffRequestsClient === null) {
          return this.getStaffRequestedClient(state, parseInt(clientId, 10));
        } else if (this.dataService.user.is_staff === false && this.dataService.outsourcedUser === false) {
          if (isNaN(clientId) === false && isNaN(farmId) === false) {
            return this._farmsService.getClientFarms(clientId);
          }
          return of(true);
        } else if (this.dataService.staff || this.dataService.outsourcedUser === true) {
          return of(this._handleRoutingOfStaffMembersOutOfAeroview());
        }
      }),
      switchMap(() => {
        // If there is a farm in question, make sure that the route is legal
        if (isNaN(clientId) === false && isNaN(farmId) === false) {
          return this.dataService.ensureLegalRoute(parseInt(farmId, 10));
        } else if (!isNaN(clientId) && !isNaN(policyId)) {
          return this.dataService.ensureLegalRouteForPolicy(parseInt(clientId, 10), parseInt(policyId, 10));
        }
        return of(true);
      }),
      tap(() => {
        // Fetch any additional data relating to the client (crops, scout missions etc)
        if (this.dataService.user.is_staff === false && this.dataService.outsourcedUser === false) {
          this.dataService.fetchAdditionalData();
        }
        this.dataService.loadingApp = false;
      }),
      catchError((error) => {
        console.log('Some error', error);
        this.dataService.loadingApp = false;
        return of(false);
      })
    );
  }

  /** The confirm token request updates the cookies as relevant and then continues as normal **/
  confirmTokensThenReturnUserData(state: RouterStateSnapshot): Observable<boolean> {
    return this.authHttp.get('gateway/auth/refresh_id_token/').pipe(
      flatMap((resp: { user_id: number; token: string; }) => {
        return this.getUserData(resp.user_id, state, null);
      }),
      tap(() => {
        this.dataService.loadingApp = false;
      }),
      catchError(() => {
        this.dataService.loadingApp = false;
        this.router.navigateByUrl('/log-in?auto_login=false');
        return of(false);
      })
    );
  }

  isUserAuthorized = (state): Observable<boolean> => {
    // THIS WILL ONLY WORK WHEN LIVE, WILL NEED A WORKAROUND FOR LOCAL DEV WHEN MOVING OVER COMPLETELY
    const userIdFromCookie = getCookie('Aeroview-UserId');
    const userId = userIdFromCookie !== '' ? parseInt(userIdFromCookie, 10) : null;
    // Make sure there is an 'aeroUser' local storage key
    const user = JSON.parse(localStorage.getItem('aeroUser'));
    // If the user is already confirmed then return true
    if (this.dataService.userConfirmed) {
      return of(true);
    } else if (user !== null || userId !== null) {
      this.dataService.loadingApp = true;
      return this.confirmTokensThenReturnUserData(state);
    } else {
      return of(false);
    }
  }

  hasAuthorisedInsuranceAccessToFarm = (state): Observable<boolean> => {
    // Ensure uploader works regardless of product access
    if (state.url.indexOf("upload-data") > 0) {
      return of(true);
    }
    const fourthIndexSplit = state.url.split("/")[3];
    if (fourthIndexSplit === undefined || isNaN(parseInt(fourthIndexSplit))) {
      return of(true);
    }
    const farmId = fourthIndexSplit;
    return this._farmsService.isNotAnInsuranceFarmOrIsStaffUser(
      this.dataService.staff,
      farmId
    );
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean>|boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    return this.isUserAuthorized(state).pipe(
      switchMap((userIsAuthorised) => {
        return forkJoin([
          of(userIsAuthorised),
          this.hasAuthorisedInsuranceAccessToFarm(state),
        ]);
      }),
      tap(([userIsAuthorised, userHasFarmAccess]) => {
        if (!userIsAuthorised) {
          this.dataService.loadingApp = false;
          this.router.navigateByUrl("/log-in?auto_login=false");
        }
        if (!userHasFarmAccess) {
          // If user access is denied, these attributes are defined
          const clientId = state.url.split("/")[2];
          const farmId = state.url.split("/")[3];
          window.location.href = `${NEW_PLATFORM_URL}/${clientId}/${farmId}/dashboard`;
        }
      }),
      map(([userIsAuthorised, userHasFarmAccess]) => {
        return userIsAuthorised && userHasFarmAccess;
      })
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let canActivate = false;
    // Check if the user is able to activate a specific state
    const userRoles = route.data['userRoles'];
    for (let i = 0; i < userRoles.length; i++) {
      if (this.dataService[userRoles[i]]) {
        canActivate = true;
      }
    }
    // If the user at the least can activate then check if there are additional permissions
    if (canActivate) {
      // If there are additional permissions required then check against the group of the user
      if ('data' in route.routeConfig && route.routeConfig.data.additionalPermissions) {
        return this.dataService.staffViewablePages.indexOf(route.routeConfig.path) !== -1;
      } else {
        return true;
      }
    }
    // If nothing has returned here yet, check the user group and see if can continue
    return false;
  }
}
